// DS3.1, DS3.7
import React from 'react';
import InputErrorMessage from '../../lego/InputErrorMessage';
import InputField, { type InputFieldProps } from '../../lego/InputField';
import InputHint from '../../lego/InputHint';
import InputLabelSubText from '../../lego/InputLabelSubText';
import InputLabelText from '../../lego/InputLabelText';
import InputSuccessMessage from '../../lego/InputSuccessMessage';
import LimitedTextInputField from '../../lego/LimitedTextInputField';
import FormElement, { type FormElementProps } from '../../layouts/FormElement';

export interface Props
  extends InputFieldProps,
    Pick<
      FormElementProps,
      'noBottomSpacing' | 'slimBottomSpacing' | 'isLabelHidden'
    > {
  /** Required for a11y reasons. We need to be able to link autocomplete menus and labels to the input field. */
  id: string;

  /**
   * Displays above the input field.
   *
   * Required for a11y reasons. If design won't work a label into the spec, consider passing through `isLabelHidden`.
   */
  label: string;

  /** Displays below the `label` in smaller font */
  subLabel?: string;

  /**
   * Displays below the input field.
   *
   * Depending on the value of `validationResult`, it'll be wrapped in...
   *
   * `error`: `<InputErrorMessage />`
   *
   * `success`: `<InputSuccessMessage />`
   *
   * `undefined`: `<InputHint />`
   *
   */
  details?: React.ReactNode;

  /** A class name to be used in the FormElement component that wraps the input */
  wrapperClassName?: string;

  /** Hide the max length counter */
  maxLengthHidden?: boolean;
}

/**
 * A single line input field with a label and some details.
 */
const TextInput = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      id,
      label,
      isLabelHidden,
      subLabel,
      isRequired,
      details,
      validationResult,
      noBottomSpacing,
      slimBottomSpacing,
      maxLengthHidden,
      wrapperClassName,
      ...rest
    },
    ref
  ) => {
    let wrappedDetails: React.ReactNode = null;
    const errorMessageId = `${id}-error`;
    const hintId = `${id}-hint`;

    switch (validationResult) {
      case 'success':
        wrappedDetails = details && (
          <InputSuccessMessage>{details}</InputSuccessMessage>
        );
        break;
      case 'error':
        wrappedDetails = details && (
          <InputErrorMessage id={errorMessageId}>{details}</InputErrorMessage>
        );
        break;
      default:
        wrappedDetails = details && (
          <InputHint id={hintId}>{details}</InputHint>
        );
    }

    const commonInputProps = {
      id,
      isRequired,
      validationResult,
      'aria-errormessage':
        validationResult === 'error' ? errorMessageId : undefined,
      'aria-describedby':
        validationResult === undefined && details ? hintId : undefined,
      ref,
    };

    return (
      <FormElement
        inputId={id}
        label={<InputLabelText isRequired={isRequired}>{label}</InputLabelText>}
        isLabelHidden={isLabelHidden}
        subLabel={subLabel && <InputLabelSubText>{subLabel}</InputLabelSubText>}
        details={wrappedDetails}
        noBottomSpacing={noBottomSpacing}
        slimBottomSpacing={slimBottomSpacing}
        className={wrapperClassName}
      >
        {rest.maxLength === undefined || maxLengthHidden ? (
          <InputField {...commonInputProps} {...rest} />
        ) : (
          <LimitedTextInputField
            maxLength={rest.maxLength}
            {...commonInputProps}
            {...rest}
          />
        )}
      </FormElement>
    );
  }
);
TextInput.displayName = 'TextInput';

export default TextInput;
